/**
 * Raw Materials Module - Supplies & Raw Materials Management
 * Full GDP Compliance with receiving workflow, QA review process, and audit trail
 */
import React, { useState, useEffect, useCallback } from 'react';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Alert, Collapse, FormControlLabel,
  Checkbox, Select, MenuItem, FormControl, InputLabel, InputAdornment, Tooltip,
  Paper, Stack, CircularProgress, Snackbar, Stepper, Step, StepLabel, Divider,
  FormHelperText, RadioGroup, Radio, FormLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocalShipping as ReceiveIcon,
  CheckCircle as ApprovedIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  Assignment as ReviewIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewIcon,
  AcUnit as FrozenIcon,
  Thermostat as RefrigeratedIcon,
  WbSunny as AmbientIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useOrg } from '../../contexts/OrgContext';
import { rawMaterialsAPI, rawMaterialKitsAPI } from '../../services/api';
import KitSelectionModal from '../../components/rawMaterials/KitSelectionModal';
import KitCreateEditDialog from '../../components/rawMaterials/KitCreateEditDialog';
import KitReceiveDialog from '../../components/rawMaterials/KitReceiveDialog';
import { formatDateCST, getTodayCST, formatDateForInput } from '../../utils/dateUtils';

// Alias for backward compatibility within this file
const formatDate = formatDateCST;

// ============================================
// MAIN COMPONENT
// ============================================

const RawMaterials = () => {
  const { orgUpper } = useOrg();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  const [stats, setStats] = useState({ totalItems: 0, totalApprovedQty: 0, totalAwaitingQAQty: 0, lowStockCount: 0 });

  // Mobile navigation states
  const [currentMode, setCurrentMode] = useState('review'); // 'review' or 'entry'
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveItem, setReceiveItem] = useState(null);
  const [qaReviewDialogOpen, setQAReviewDialogOpen] = useState(false);
  const [qaReviewReceiving, setQAReviewReceiving] = useState(null);
  const [continueReceivingId, setContinueReceivingId] = useState(null);
  const [viewApprovedDialogOpen, setViewApprovedDialogOpen] = useState(false);
  const [viewApprovedReceiving, setViewApprovedReceiving] = useState(null);
  
  // Kit dialog states
  const [kitSelectionOpen, setKitSelectionOpen] = useState(false);
  const [kitCreateEditOpen, setKitCreateEditOpen] = useState(false);
  const [kitReceiveOpen, setKitReceiveOpen] = useState(false);
  const [selectedKit, setSelectedKit] = useState(null);
  const [editingKit, setEditingKit] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch items
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await rawMaterialsAPI.getItems(orgUpper);
      if (response.success) {
        setItems(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch raw materials:', error);
      setSnackbar({ open: true, message: 'Failed to load raw materials', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [orgUpper]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await rawMaterialsAPI.getStats(orgUpper);
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [orgUpper]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Auto-switch to review mode on mobile when switching from desktop
      if (mobile && !isMobile) {
        setCurrentMode('review');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Initial data load - only runs when organization changes
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (!isMounted) return;
      await fetchItems();
      if (!isMounted) return;
      await fetchStats();
    };
    
    loadData();
    
    return () => { isMounted = false; };
  }, [orgUpper]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExpandItem = async (itemId) => {
    if (expandedItem === itemId) {
      setExpandedItem(null);
    } else {
      try {
        const response = await rawMaterialsAPI.getItem(itemId);
        if (response.success) {
          setItems(prev => prev.map(item => 
            item.id === itemId ? { ...item, receivings: response.data.receivings } : item
          ));
        }
      } catch (error) {
        console.error('Failed to fetch item details:', error);
      }
      setExpandedItem(itemId);
    }
  };

  const handleContinueReceiving = async (receivingId) => {
    try {
      const response = await rawMaterialsAPI.getReceiving(receivingId);
      if (response.success) {
        setContinueReceivingId(receivingId);
        setReceiveItem(response.data.rawMaterialItem);
        setReceiveDialogOpen(true);
      }
    } catch (error) {
      console.error('Failed to fetch receiving details:', error);
      setSnackbar({ open: true, message: 'Failed to load receiving details', severity: 'error' });
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'APPROVED': { color: '#22c55e', bg: '#dcfce7', label: 'Approved' },
      'QA_REVIEW': { color: '#f97316', bg: '#ffedd5', label: 'QA Review' },
      'RECEIVING_IN_PROGRESS': { color: '#eab308', bg: '#fef9c3', label: 'Receiving In Progress' },
      'REJECTED': { color: '#ef4444', bg: '#fee2e2', label: 'Rejected' },
    };
    const config = statusConfig[status] || { color: '#6b7280', bg: '#f3f4f6', label: status };
    
    return (
      <Chip 
        label={config.label} 
        size="small" 
        sx={{ 
          bgcolor: config.bg, 
          color: config.color, 
          fontWeight: 600,
          border: `1px solid ${config.color}20`
        }} 
      />
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };


  const handleRefresh = () => {
    fetchItems();
    fetchStats();
  };

  // Kit handlers
  const handleSelectKit = (kit) => {
    setSelectedKit(kit);
    setKitSelectionOpen(false);
    setKitReceiveOpen(true);
  };

  const handleCreateKit = () => {
    setKitSelectionOpen(false);
    setEditingKit(null);
    setKitCreateEditOpen(true);
  };

  const handleEditKit = (kit) => {
    setKitSelectionOpen(false);
    setEditingKit(kit);
    setKitCreateEditOpen(true);
  };

  const handleKitSaved = () => {
    fetchItems(); // Refresh in case we need to update any references
    setSnackbar({ open: true, message: 'Kit saved successfully', severity: 'success' });
  };

  const handleKitReceived = () => {
    setSnackbar({ open: true, message: 'Kit receiving session saved', severity: 'success' });
    fetchItems(); // Refresh items in case quantities changed
  };

  // Mobile navigation handlers
  const handleModeChange = (mode) => {
    setCurrentMode(mode);
  };

  if (loading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: '#039582' }} />
      </Box>
    );
  }

  // Mobile Navigation Component
  const MobileNavigation = () => (
    <Box sx={{
      display: { xs: 'block', md: 'none' },
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      bgcolor: 'white',
      borderBottom: '1px solid #e5e7eb',
      px: 2,
      py: 1
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Supplies & Raw Materials
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReceiveIcon />}
            onClick={() => setKitSelectionOpen(true)}
            sx={{ fontSize: '0.8rem', mr: 1 }}
          >
            Receive Kit
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => { setEditItem(null); setCreateDialogOpen(true); }}
            sx={{ fontSize: '0.8rem' }}
          >
            Add Item
          </Button>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', mt: 1, border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden' }}>
        <Button
          fullWidth
          variant={currentMode === 'review' ? 'contained' : 'text'}
          onClick={() => handleModeChange('review')}
          sx={{
            borderRadius: 0,
            py: 1,
            fontSize: '0.85rem',
            fontWeight: currentMode === 'review' ? 600 : 400,
            bgcolor: currentMode === 'review' ? '#039582' : 'transparent',
            color: currentMode === 'review' ? 'white' : '#64748b',
            '&:hover': {
              bgcolor: currentMode === 'review' ? '#027A6B' : '#f8fafc'
            }
          }}
        >
          Review Items
        </Button>
        <Button
          fullWidth
          variant={currentMode === 'entry' ? 'contained' : 'text'}
          onClick={() => handleModeChange('entry')}
          sx={{
            borderRadius: 0,
            py: 1,
            fontSize: '0.85rem',
            fontWeight: currentMode === 'entry' ? 600 : 400,
            bgcolor: currentMode === 'entry' ? '#039582' : 'transparent',
            color: currentMode === 'entry' ? 'white' : '#64748b',
            '&:hover': {
              bgcolor: currentMode === 'entry' ? '#027A6B' : '#f8fafc'
            }
          }}
        >
          Quick Entry
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Breadcrumbs />
      {/* Mobile Navigation */}
      <MobileNavigation />

      {/* Header Banner - Desktop Only */}
      <Box sx={{
        mb: 3,
        mt: { xs: '0px', md: '0px' },
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        color: 'white',
        display: { xs: 'none', md: 'flex' },
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: 2,
        boxShadow: '0 4px 12px rgba(3, 149, 130, 0.3)'
      }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
            Supplies & Raw Materials - EPL
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            GDP-compliant raw material tracking with receiving workflow and QA review
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh">
            <IconButton 
              onClick={handleRefresh} 
              sx={{ 
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<ReceiveIcon />}
            onClick={() => setKitSelectionOpen(true)}
            sx={{
              borderColor: 'white',
              color: 'white',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Receive Kit
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setEditItem(null); setCreateDialogOpen(true); }}
            sx={{
              bgcolor: 'white',
              color: '#039582',
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(255, 255, 255, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Create New Item
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{
            bgcolor: '#f0fdfa',
            border: '1px solid #0d9488',
            minHeight: { xs: '80px', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
              <Typography variant="h5" sx={{ color: '#0d9488', fontWeight: 700, fontSize: { xs: '1.25rem', md: '2rem' } }}>
                {stats.totalItems || items.length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                Total Items
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{
            bgcolor: '#dcfce7',
            border: '1px solid #22c55e',
            minHeight: { xs: '80px', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
              <Typography variant="h5" sx={{ color: '#22c55e', fontWeight: 700, fontSize: { xs: '1.25rem', md: '2rem' } }}>
                {stats.totalApprovedQty || items.reduce((sum, i) => sum + (i.approvedQty || 0), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                Approved Units
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{
            bgcolor: '#ffedd5',
            border: '1px solid #f97316',
            minHeight: { xs: '80px', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
              <Typography variant="h5" sx={{ color: '#f97316', fontWeight: 700, fontSize: { xs: '1.25rem', md: '2rem' } }}>
                {stats.totalAwaitingQAQty || items.reduce((sum, i) => sum + (i.awaitingQAReviewQty || 0), 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                Awaiting QA
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card sx={{
            bgcolor: '#fee2e2',
            border: '1px solid #ef4444',
            minHeight: { xs: '80px', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
              <Typography variant="h5" sx={{ color: '#ef4444', fontWeight: 700, fontSize: { xs: '1.25rem', md: '2rem' } }}>
                {stats.lowStockCount || items.filter(i => i.needsReorder).length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                Low Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Low Stock Alert */}
      {(stats.lowStockCount > 0 || items.filter(i => i.needsReorder).length > 0) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>{stats.lowStockCount || items.filter(i => i.needsReorder).length} item(s)</strong> are at or below reorder level. Review and reorder soon.
        </Alert>
      )}

      {/* Main Content - Desktop Table / Mobile Cards */}
      {isMobile && currentMode === 'entry' ? (
        /* Mobile Quick Entry Interface */
        <QuickEntryInterface
          items={items}
          onReceiveItem={(item) => { setContinueReceivingId(null); setReceiveItem(item); setReceiveDialogOpen(true); }}
          onCreateNew={() => { setEditItem(null); setCreateDialogOpen(true); }}
        />
      ) : (
        /* Review Interface - Table on Desktop, Cards on Mobile */
        <Card>
          <CardContent sx={{ p: { xs: 0, md: 2 } }}>
            {isMobile ? (
              /* Mobile Card View */
              <Box>
                {items.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>No raw materials found.</Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => { setEditItem(null); setCreateDialogOpen(true); }}
                    >
                      Create your first item
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ p: 2 }}>
                    {items.map((item) => (
                      <RawMaterialMobileCard
                        key={item.id}
                        item={item}
                        expanded={expandedItem === item.id}
                        onExpand={() => handleExpandItem(item.id)}
                        onReceive={() => { setContinueReceivingId(null); setReceiveItem(item); setReceiveDialogOpen(true); }}
                        onEdit={() => { setEditItem(item); setCreateDialogOpen(true); }}
                        onContinueReceiving={handleContinueReceiving}
                        onQAReview={(receiving) => { setQAReviewReceiving(receiving); setQAReviewDialogOpen(true); }}
                        onViewApproved={(receiving) => { setViewApprovedReceiving(receiving); setViewApprovedDialogOpen(true); }}
                        formatCurrency={formatCurrency}
                        formatDate={formatDate}
                        getStatusChip={getStatusChip}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              /* Desktop Table View */
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#1a5f5a' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Item Description</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Vendor / Manufacturer</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Certificate</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Storage Requirements</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Approved Qty</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Awaiting QA Qty</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Total Qty</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="right">Avg Cost/Item</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No raw materials found. Create your first item to get started.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : items.map((item) => {
                      // Format vendor/manufacturer for display
                      const vendorManufacturer = item.vendor && item.manufacturer && item.vendor === item.manufacturer
                        ? item.vendor
                        : item.vendor && item.manufacturer
                        ? `${item.vendor} / ${item.manufacturer}`
                        : item.vendor || item.manufacturer || '-';

                      // Format certificate display
                      const certificateDisplay = item.certificateRequired
                        ? 'Yes (COA / COC / COS)'
                        : 'No';

                      return (
                      <React.Fragment key={item.id}>
                        {/* Main Row */}
                        <TableRow
                          hover
                          sx={{
                            cursor: 'pointer',
                            bgcolor: item.needsReorder ? '#fef2f2' : 'inherit'
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {item.needsReorder && (
                                <Tooltip title="Low Stock - Reorder Needed">
                                  <WarningIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                                </Tooltip>
                              )}
                              <Box>
                                <Typography fontWeight={600}>{item.itemDescription}</Typography>
                                {item.sku && (
                                  <Typography variant="caption" color="text.secondary">SKU: {item.sku}</Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{vendorManufacturer}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{
                              color: item.certificateRequired ? '#1a5f5a' : 'text.secondary',
                              fontWeight: item.certificateRequired ? 600 : 400
                            }}>
                              {certificateDisplay}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {item.storageRequirements || [
                                item.storageAmbient && 'Ambient',
                                item.storageFrozen && 'Frozen',
                                item.storageRefrigerated && 'Refrigerated',
                              ].filter(Boolean).join(', ') || '-'}
                            </Typography>
                            {item.storageTemperatureRange && (
                              <Typography variant="caption" color="text.secondary">
                                ({item.storageTemperatureRange})
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={item.approvedQty || 0} size="small" sx={{ bgcolor: '#dcfce7', color: '#22c55e', fontWeight: 600 }} />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={item.awaitingQAReviewQty || 0} size="small" sx={{ bgcolor: '#ffedd5', color: '#f97316', fontWeight: 600 }} />
                          </TableCell>
                          <TableCell align="center">
                            <Typography fontWeight={600}>{item.totalQty || 0}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600}>{formatCurrency(item.averageCost)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                              <Tooltip title="Receive Supplies">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setContinueReceivingId(null); setReceiveItem(item); setReceiveDialogOpen(true); }}
                                  sx={{ color: '#039582' }}
                                >
                                  <ReceiveIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Item">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setEditItem(item); setCreateDialogOpen(true); }}
                                  sx={{ color: '#6366f1' }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                size="small"
                                onClick={() => handleExpandItem(item.id)}
                                sx={{ color: '#64748b' }}
                              >
                                {expandedItem === item.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Expanded Row - Receivings */}
                        <TableRow>
                          <TableCell colSpan={9} sx={{ p: 0, borderBottom: expandedItem === item.id ? '2px solid #e2e8f0' : 'none' }}>
                            <Collapse in={expandedItem === item.id} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: '#f8fafc' }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1a5f5a' }}>
                                  Receiving History
                                </Typography>
                                <Table size="small">
                                  <TableHead sx={{ bgcolor: '#e2e8f0' }}>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600 }}>Date Received</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Qty Received</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Lot Number</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Expiration Date</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>Cost/Item</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }}>QA Status</TableCell>
                                      <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(!item.receivings || item.receivings.length === 0) ? (
                                      <TableRow>
                                        <TableCell colSpan={7} align="center">
                                          <Typography color="text.secondary" sx={{ py: 2 }}>No receiving records yet</Typography>
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      item.receivings.map((receiving) => {
                                        // Check if expired
                                        const isExpired = !receiving.expirationDateNA && receiving.expirationDate &&
                                          new Date(receiving.expirationDate) < new Date();

                                        return (
                                        <TableRow
                                          key={receiving.id}
                                          hover
                                          sx={{
                                            bgcolor: isExpired && receiving.status === 'APPROVED' ? '#fee2e2' : 'inherit',
                                            opacity: isExpired && receiving.status === 'APPROVED' ? 0.8 : 1
                                          }}
                                        >
                                          <TableCell>{formatDate(receiving.dateReceived)}</TableCell>
                                          <TableCell>{receiving.qtyReceived}</TableCell>
                                          <TableCell>{receiving.lotNumberNA ? 'N/A' : receiving.lotNumber || '-'}</TableCell>
                                          <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                              <Typography variant="body2">
                                                {receiving.expirationDateNA ? 'N/A' : formatDate(receiving.expirationDate)}
                                              </Typography>
                                              {isExpired && receiving.status === 'APPROVED' && (
                                                <Chip
                                                  label="EXPIRED"
                                                  size="small"
                                                  color="error"
                                                  sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                                />
                                              )}
                                            </Stack>
                                          </TableCell>
                                          <TableCell>{formatCurrency(receiving.costPerItem)}</TableCell>
                                          <TableCell>{getStatusChip(receiving.status)}</TableCell>
                                          <TableCell align="center">
                                            {receiving.status === 'RECEIVING_IN_PROGRESS' && (
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleContinueReceiving(receiving.id)}
                                                sx={{ textTransform: 'none' }}
                                              >
                                                Continue
                                              </Button>
                                            )}
                                            {receiving.status === 'QA_REVIEW' && (
                                              <Button
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                onClick={async () => {
                                                  // Fetch full receiving details with rawMaterialItem
                                                  try {
                                                    const response = await rawMaterialsAPI.getReceiving(receiving.id);
                                                    if (response.success) {
                                                      setQAReviewReceiving(response.data);
                                                      setQAReviewDialogOpen(true);
                                                    }
                                                  } catch (error) {
                                                    console.error('Failed to fetch receiving details:', error);
                                                    // Fallback to using the receiving object we have
                                                    setQAReviewReceiving(receiving);
                                                    setQAReviewDialogOpen(true);
                                                  }
                                                }}
                                                sx={{ textTransform: 'none' }}
                                              >
                                                Review
                                              </Button>
                                            )}
                                            {receiving.status === 'APPROVED' && (
                                              <Tooltip title="View approved details, notes, and certificate">
                                                <IconButton
                                                  size="small"
                                                  onClick={async () => {
                                                    // Fetch full receiving details
                                                    try {
                                                      const response = await rawMaterialsAPI.getReceiving(receiving.id);
                                                      if (response.success) {
                                                        setViewApprovedReceiving(response.data);
                                                        setViewApprovedDialogOpen(true);
                                                      }
                                                    } catch (error) {
                                                      console.error('Failed to fetch receiving details:', error);
                                                      setSnackbar({ open: true, message: 'Failed to load receiving details', severity: 'error' });
                                                    }
                                                  }}
                                                  sx={{ color: '#22c55e' }}
                                                >
                                                  <ViewIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                        );
                                      })
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Item Dialog */}
      <CreateEditItemDialog 
        open={createDialogOpen}
        onClose={() => { setCreateDialogOpen(false); setEditItem(null); }}
        item={editItem}
        onSuccess={() => { fetchItems(); fetchStats(); setCreateDialogOpen(false); setEditItem(null); setSnackbar({ open: true, message: editItem ? 'Item updated successfully' : 'Item created successfully', severity: 'success' }); }}
        organization={orgUpper}
      />

      {/* Receive Dialog */}
      <ReceiveSuppliesDialog
        open={receiveDialogOpen}
        onClose={() => { setReceiveDialogOpen(false); setReceiveItem(null); setContinueReceivingId(null); }}
        item={receiveItem}
        existingReceivingId={continueReceivingId}
        onSuccess={(msg) => { fetchItems(); fetchStats(); setReceiveDialogOpen(false); setReceiveItem(null); setContinueReceivingId(null); setSnackbar({ open: true, message: msg || 'Receiving saved successfully', severity: 'success' }); }}
      />

      {/* QA Review Dialog */}
      <QAReviewDialog
        open={qaReviewDialogOpen}
        onClose={() => { setQAReviewDialogOpen(false); setQAReviewReceiving(null); }}
        receiving={qaReviewReceiving}
        onSuccess={(msg) => { fetchItems(); fetchStats(); setQAReviewDialogOpen(false); setQAReviewReceiving(null); setSnackbar({ open: true, message: msg || 'QA Review completed', severity: 'success' }); }}
      />

      {/* View Approved Dialog */}
      <ViewApprovedDialog
        open={viewApprovedDialogOpen}
        onClose={() => { setViewApprovedDialogOpen(false); setViewApprovedReceiving(null); }}
        receiving={viewApprovedReceiving}
        onInventoryUpdated={(msg) => { 
          fetchItems(); 
          fetchStats(); 
          setSnackbar({ open: true, message: msg || 'Inventory adjusted successfully', severity: 'success' }); 
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Kit Selection Modal */}
      <KitSelectionModal
        open={kitSelectionOpen}
        onClose={() => setKitSelectionOpen(false)}
        onSelectKit={handleSelectKit}
        onCreateKit={handleCreateKit}
        onEditKit={handleEditKit}
        organization={orgUpper}
      />

      {/* Kit Create/Edit Dialog */}
      <KitCreateEditDialog
        open={kitCreateEditOpen}
        onClose={() => {
          setKitCreateEditOpen(false);
          setEditingKit(null);
        }}
        kit={editingKit}
        organization={orgUpper}
        onSuccess={handleKitSaved}
      />

      {/* Kit Receive Dialog */}
      <KitReceiveDialog
        open={kitReceiveOpen}
        onClose={() => {
          setKitReceiveOpen(false);
          setSelectedKit(null);
        }}
        kit={selectedKit}
        organization={orgUpper}
        onSuccess={handleKitReceived}
      />
    </Box>
  );
};

// ============================================
// MOBILE RAW MATERIAL CARD COMPONENT
// ============================================

const RawMaterialMobileCard = ({
  item,
  expanded,
  onExpand,
  onReceive,
  onEdit,
  onContinueReceiving,
  onQAReview,
  onViewApproved,
  formatCurrency,
  formatDate,
  getStatusChip
}) => {
  // Format vendor/manufacturer for display
  const vendorManufacturer = item.vendor && item.manufacturer && item.vendor === item.manufacturer
    ? item.vendor
    : item.vendor && item.manufacturer
    ? `${item.vendor} / ${item.manufacturer}`
    : item.vendor || item.manufacturer || '-';

  const certificateDisplay = item.certificateRequired
    ? 'Certificate Required'
    : 'No Certificate';

  const storageTypes = [
    item.storageAmbient && 'Ambient',
    item.storageFrozen && 'Frozen',
    item.storageRefrigerated && 'Refrigerated',
  ].filter(Boolean);

  return (
    <Card sx={{
      mb: 2,
      border: item.needsReorder ? '2px solid #ef4444' : '1px solid #e5e7eb',
      bgcolor: item.needsReorder ? '#fef2f2' : 'white'
    }}>
      <CardContent sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {item.needsReorder && (
                <WarningIcon sx={{ color: '#ef4444', fontSize: 18 }} />
              )}
              <Typography variant="subtitle1" fontWeight={600} sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {item.itemDescription}
              </Typography>
            </Box>
            {item.sku && (
              <Typography variant="caption" color="text.secondary">SKU: {item.sku}</Typography>
            )}
          </Box>
          <IconButton onClick={onExpand} size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {/* Key Info Row */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`${item.approvedQty || 0} Approved`}
            size="small"
            sx={{ bgcolor: '#dcfce7', color: '#22c55e', fontWeight: 600 }}
          />
          <Chip
            label={`${item.awaitingQAReviewQty || 0} QA`}
            size="small"
            sx={{ bgcolor: '#ffedd5', color: '#f97316', fontWeight: 600 }}
          />
          <Chip
            label={`Total: ${item.totalQty || 0}`}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* Details Grid */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">Vendor</Typography>
            <Typography variant="body2" fontWeight={500} sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {vendorManufacturer}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">Cost/Item</Typography>
            <Typography variant="body2" fontWeight={600}>
              {formatCurrency(item.averageCost)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">Certificate</Typography>
            <Typography variant="body2" sx={{
              color: item.certificateRequired ? '#1a5f5a' : 'text.secondary',
              fontWeight: item.certificateRequired ? 600 : 400
            }}>
              {certificateDisplay}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" display="block">Storage</Typography>
            <Typography variant="body2">
              {storageTypes.length > 0 ? storageTypes.join(', ') : '-'}
            </Typography>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<ReceiveIcon />}
            onClick={onReceive}
            sx={{ flex: 1, bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
          >
            Receive
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onEdit}
            sx={{ flex: 1 }}
          >
            Edit
          </Button>
        </Box>

        {/* Expanded Content */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e5e7eb' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1a5f5a' }}>
              Receiving History
            </Typography>

            {(!item.receivings || item.receivings.length === 0) ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No receiving records yet
              </Typography>
            ) : (
              <Stack spacing={1}>
                {item.receivings.map((receiving) => {
                  const isExpired = !receiving.expirationDateNA && receiving.expirationDate &&
                    new Date(receiving.expirationDate) < new Date();

                  return (
                    <Paper key={receiving.id} sx={{
                      p: 1.5,
                      bgcolor: isExpired && receiving.status === 'APPROVED' ? '#fee2e2' : '#f8fafc'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {formatDate(receiving.dateReceived)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Qty: {receiving.qtyReceived} | Lot: {receiving.lotNumberNA ? 'N/A' : receiving.lotNumber || '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isExpired && receiving.status === 'APPROVED' && (
                            <Chip label="EXPIRED" size="small" color="error" sx={{ fontSize: '0.7rem' }} />
                          )}
                          {getStatusChip(receiving.status)}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatCurrency(receiving.costPerItem)}
                        </Typography>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {receiving.status === 'RECEIVING_IN_PROGRESS' && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => onContinueReceiving(receiving.id)}
                              sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
                            >
                              Continue
                            </Button>
                          )}
                          {receiving.status === 'QA_REVIEW' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => onQAReview(receiving)}
                              sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
                            >
                              Review
                            </Button>
                          )}
                          {receiving.status === 'APPROVED' && (
                            <IconButton
                              size="small"
                              onClick={() => onViewApproved(receiving)}
                              sx={{ color: '#22c55e' }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

// ============================================
// MOBILE QUICK ENTRY INTERFACE
// ============================================

const QuickEntryInterface = ({ items, onReceiveItem, onCreateNew }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [recentItems, setRecentItems] = useState([]);

  // Filter items based on search
  const filteredItems = items.filter(item =>
    item.itemDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.sku && item.sku.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Get recently used items (mock - in real app this would come from API)
  useEffect(() => {
    const recent = items.slice(0, 5); // Just show first 5 for demo
    setRecentItems(recent);
  }, [items]);

  return (
    <Box>
      {/* Quick Actions */}
      <Card sx={{ mb: 2, bgcolor: '#f0fdfa', border: '1px solid #0d9488' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: '#1a5f5a' }}>
            Quick Actions
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreateNew}
                sx={{
                  bgcolor: '#039582',
                  '&:hover': { bgcolor: '#027A6B' },
                  py: 1.5
                }}
              >
                New Item
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<InventoryIcon />}
                sx={{
                  borderColor: '#039582',
                  color: '#039582',
                  py: 1.5,
                  '&:hover': { borderColor: '#027A6B', bgcolor: '#f0fdfa' }
                }}
              >
                Inventory
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Search */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Search items by name or SKU..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {searchTerm ? (
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a5f5a' }}>
              Search Results ({filteredItems.length})
            </Typography>
          ) : (
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#1a5f5a' }}>
              Recent Items
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Items List */}
      <Box>
        {(searchTerm ? filteredItems : recentItems).length === 0 ? (
          <Card>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {searchTerm ? 'No items found matching your search.' : 'No recent items.'}
              </Typography>
              {!searchTerm && (
                <Button variant="outlined" onClick={onCreateNew}>
                  Create your first item
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          (searchTerm ? filteredItems : recentItems).map((item) => (
            <Card key={item.id} sx={{ mb: 1 }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mb: 0.5
                    }}>
                      {item.itemDescription}
                    </Typography>
                    {item.sku && (
                      <Typography variant="caption" color="text.secondary">
                        SKU: {item.sku}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={`${item.approvedQty || 0} Available`}
                    size="small"
                    sx={{ bgcolor: '#dcfce7', color: '#22c55e', fontSize: '0.7rem' }}
                  />
                  {item.needsReorder && (
                    <Chip
                      label="Low Stock"
                      size="small"
                      sx={{ bgcolor: '#fee2e2', color: '#ef4444', fontSize: '0.7rem' }}
                    />
                  )}
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ReceiveIcon />}
                  onClick={() => onReceiveItem(item)}
                  sx={{
                    bgcolor: '#039582',
                    '&:hover': { bgcolor: '#027A6B' }
                  }}
                >
                  Receive This Item
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    </Box>
  );
};

// ============================================
// CREATE/EDIT ITEM DIALOG
// ============================================

const CreateEditItemDialog = ({ open, onClose, item, onSuccess, organization }) => {
  const [formData, setFormData] = useState({
    itemDescription: '',
    standardCost: '',
    sku: '',
    vendorManufacturer: '', // Combined field
    vendor: '', // Keep for backward compatibility
    manufacturer: '', // Keep for backward compatibility
    storageAmbient: false,
    storageFrozen: false,
    storageRefrigerated: false,
    storageTemperatureRange: '',
    storageNotes: '',
    certificateRequired: false,
    reorderLevel: 10,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (item) {
      // Combine vendor and manufacturer for display
      const vendorManufacturer = item.vendor && item.manufacturer && item.vendor === item.manufacturer
        ? item.vendor
        : item.vendor && item.manufacturer
        ? `${item.vendor} / ${item.manufacturer}`
        : item.vendor || item.manufacturer || '';
      
      setFormData({
        itemDescription: item.itemDescription || '',
        standardCost: item.standardCost?.toString() || '',
        sku: item.sku || '',
        vendorManufacturer: vendorManufacturer,
        vendor: item.vendor || '',
        manufacturer: item.manufacturer || '',
        storageAmbient: item.storageAmbient || false,
        storageFrozen: item.storageFrozen || false,
        storageRefrigerated: item.storageRefrigerated || false,
        storageTemperatureRange: item.storageTemperatureRange || '',
        storageNotes: item.storageNotes || '',
        certificateRequired: item.certificateRequired || false,
        reorderLevel: item.reorderLevel || 10,
      });
    } else {
      setFormData({
        itemDescription: '',
        standardCost: '',
        sku: '',
        vendorManufacturer: '',
        vendor: '',
        manufacturer: '',
        storageAmbient: false,
        storageFrozen: false,
        storageRefrigerated: false,
        storageTemperatureRange: '',
        storageNotes: '',
        certificateRequired: false,
        reorderLevel: 10,
      });
    }
    setError('');
  }, [item, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.itemDescription.trim()) {
      setError('Item description is required');
      return;
    }
    if (!formData.standardCost || parseFloat(formData.standardCost) < 0) {
      setError('Valid standard cost is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      // Parse vendor/manufacturer from combined field
      const vendorManufacturerValue = formData.vendorManufacturer || '';
      const parts = vendorManufacturerValue.split(' / ').map(s => s.trim());
      const vendor = parts[0] || vendorManufacturerValue || '';
      const manufacturer = parts[1] || parts[0] || vendorManufacturerValue || '';
      
      const payload = {
        ...formData,
        vendor: vendor || null,
        manufacturer: manufacturer || null,
        standardCost: parseFloat(formData.standardCost),
        reorderLevel: parseInt(formData.reorderLevel) || 10,
        organization,
      };
      
      // Remove vendorManufacturer from payload (not in schema)
      delete payload.vendorManufacturer;

      if (item) {
        await rawMaterialsAPI.updateItem(item.id, payload);
      } else {
        await rawMaterialsAPI.createItem(payload);
      }
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#039582', color: 'white', fontWeight: 600 }}>
        {item ? 'Edit Raw Material Item' : 'Create New Raw Material Item'}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Item Description"
              value={formData.itemDescription}
              onChange={(e) => handleChange('itemDescription', e.target.value)}
              placeholder="e.g., 18G Needle, 2mL Cryovial"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Standard Cost Per Item"
              type="number"
              value={formData.standardCost}
              onChange={(e) => handleChange('standardCost', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="SKU (Optional)"
              value={formData.sku}
              onChange={(e) => handleChange('sku', e.target.value)}
              placeholder="Internal SKU"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Vendor / Manufacturer"
              value={formData.vendorManufacturer}
              onChange={(e) => {
                const value = e.target.value;
                // Parse value: if contains " / ", split it; otherwise use same value for both
                const parts = value.split(' / ').map(s => s.trim());
                handleChange('vendorManufacturer', value);
                handleChange('vendor', parts[0] || value);
                handleChange('manufacturer', parts[1] || parts[0] || value);
              }}
              placeholder="e.g., Medline or Medline / Medline"
              helperText="Enter vendor and/or manufacturer (e.g., 'Medline' or 'Medline / Medline')"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a5f5a', mb: 1 }}>
              Storage Requirements
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormControlLabel
                control={<Checkbox checked={formData.storageAmbient} onChange={(e) => handleChange('storageAmbient', e.target.checked)} />}
                label="Ambient"
              />
              <FormControlLabel
                control={<Checkbox checked={formData.storageFrozen} onChange={(e) => handleChange('storageFrozen', e.target.checked)} />}
                label="Frozen"
              />
              <FormControlLabel
                control={<Checkbox checked={formData.storageRefrigerated} onChange={(e) => handleChange('storageRefrigerated', e.target.checked)} />}
                label="Refrigerated"
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Temperature Range"
              value={formData.storageTemperatureRange}
              onChange={(e) => handleChange('storageTemperatureRange', e.target.value)}
              placeholder="e.g., 2-8°C"
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Re-Order Level (Qty)"
              type="number"
              value={formData.reorderLevel}
              onChange={(e) => handleChange('reorderLevel', e.target.value)}
              helperText="Alert when approved qty falls at or below this level"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Storage Notes"
              value={formData.storageNotes}
              onChange={(e) => handleChange('storageNotes', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a5f5a', mb: 1 }}>
              Certificate Requirements
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={formData.certificateRequired} onChange={(e) => handleChange('certificateRequired', e.target.checked)} />}
              label="Quality Certificate Required?"
            />
            <FormHelperText sx={{ ml: 0, mt: 0.5 }}>
              If checked, a quality certificate (COA / COC / COS) must be uploaded during QA review
            </FormHelperText>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{ bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
        >
          {saving ? 'Saving...' : (item ? 'Update Item' : 'Create Item')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// RECEIVE SUPPLIES DIALOG (Multi-Step)
// ============================================

const ReceiveSuppliesDialog = ({ open, onClose, item, existingReceivingId, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    costPerItem: '',
    vendor: '',
    poNumber: '',
    dateReceived: getTodayCST(),
    lotNumber: '',
    lotNumberNA: false,
    expirationDate: '',
    expirationDateNA: false,
    qtyReceived: '',
    containerCondition: 'ACCEPTABLE',
    containerConditionNotes: '',
    packingSlipIncluded: null,
    packingSlipNotes: '',
    orderQuantityComplete: null,
    orderQuantityNotes: '',
    labelingAccurate: null,
    labelingNotes: '',
    shelfLifeAcceptable: null,
    shelfLifeNotes: '',
    storageConfirmed: false,
    storageOverride: false,
    storageOverrideValue: '',
    storageOverrideReason: '',
  });
  const [receivingId, setReceivingId] = useState(null);
  // Packing slip file management
  const [packingSlipFiles, setPackingSlipFiles] = useState([]); // Array of File objects
  const [uploadedPackingSlips, setUploadedPackingSlips] = useState([]); // Array of {name, url} from Azure
  const packingSlipInputRef = React.useRef(null);

  useEffect(() => {
    if (open && item) {
      if (existingReceivingId) {
        // Load existing receiving data
        loadExistingReceiving(existingReceivingId);
      } else {
        // Reset for new receiving
        // Format vendor/manufacturer for display (combine if both exist)
        const vendorManufacturer = item.vendor && item.manufacturer && item.vendor === item.manufacturer
          ? item.vendor
          : item.vendor && item.manufacturer
          ? `${item.vendor} / ${item.manufacturer}`
          : item.vendor || item.manufacturer || '';
        
        setFormData({
          costPerItem: item.standardCost?.toString() || '',
          vendor: vendorManufacturer, // Use combined vendor/manufacturer
          poNumber: '',
          dateReceived: getTodayCST(),
          lotNumber: '',
          lotNumberNA: false,
          expirationDate: '',
          expirationDateNA: false,
          qtyReceived: '',
          containerCondition: 'ACCEPTABLE',
          containerConditionNotes: '',
          packingSlipIncluded: null,
          packingSlipNotes: '',
          orderQuantityComplete: null,
          orderQuantityNotes: '',
          labelingAccurate: null,
          labelingNotes: '',
          shelfLifeAcceptable: null,
          shelfLifeNotes: '',
          storageConfirmed: false,
          storageOverride: false,
          storageOverrideValue: '',
          storageOverrideReason: '',
        });
        setReceivingId(null);
        setActiveStep(0);
        setPackingSlipFiles([]);
        setUploadedPackingSlips([]);
      }
      setError('');
    }
  }, [open, item, existingReceivingId]);

  const loadExistingReceiving = async (id) => {
    try {
      const response = await rawMaterialsAPI.getReceiving(id);
      if (response.success) {
        const r = response.data;
        setFormData({
          costPerItem: r.costPerItem?.toString() || '',
          vendor: r.vendor || '',
          poNumber: r.poNumber || '',
          dateReceived: formatDateForInput(r.dateReceived),
          lotNumber: r.lotNumber || '',
          lotNumberNA: r.lotNumberNA || false,
          expirationDate: formatDateForInput(r.expirationDate),
          expirationDateNA: r.expirationDateNA || false,
          qtyReceived: r.qtyReceived?.toString() || '',
          containerCondition: r.containerCondition || 'ACCEPTABLE',
          containerConditionNotes: r.containerConditionNotes || '',
          packingSlipIncluded: r.packingSlipIncluded,
          packingSlipNotes: r.packingSlipNotes || '',
          orderQuantityComplete: r.orderQuantityComplete,
          orderQuantityNotes: r.orderQuantityNotes || '',
          labelingAccurate: r.labelingAccurate,
          labelingNotes: r.labelingNotes || '',
          shelfLifeAcceptable: r.shelfLifeAcceptable,
          shelfLifeNotes: r.shelfLifeNotes || '',
          storageConfirmed: r.storageConfirmed || false,
          storageOverride: r.storageOverride || false,
          storageOverrideValue: r.storageOverrideValue || '',
          storageOverrideReason: r.storageOverrideReason || '',
        });
        setReceivingId(id);
        setActiveStep(0);
        setPackingSlipFiles([]);
        // Load existing uploaded packing slips
        if (r.packingSlipFileUrl) {
          try {
            const files = JSON.parse(r.packingSlipFileUrl);
            setUploadedPackingSlips(Array.isArray(files) ? files : [{ name: 'Packing Slip', url: r.packingSlipFileUrl }]);
          } catch {
            setUploadedPackingSlips([{ name: 'Packing Slip', url: r.packingSlipFileUrl }]);
          }
        } else {
          setUploadedPackingSlips([]);
        }
      }
    } catch (err) {
      setError('Failed to load receiving data');
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const steps = ['Basic Info', 'Inspection Checklist', 'Storage Confirmation'];

  const handleNext = () => setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  const handleBack = () => setActiveStep(prev => Math.max(prev - 1, 0));

  const validateStep = (step) => {
    switch (step) {
      case 0:
        if (!formData.costPerItem || parseFloat(formData.costPerItem) < 0) return 'Valid cost per item is required';
        if (!formData.dateReceived) return 'Date received is required';
        if (!formData.qtyReceived || parseInt(formData.qtyReceived) < 1) return 'Valid quantity is required';
        if (!formData.lotNumberNA && !formData.lotNumber) return 'Lot number is required (or mark N/A)';
        if (!formData.expirationDateNA && !formData.expirationDate) return 'Expiration date is required (or mark N/A)';
        return null;
      case 1:
        if (formData.packingSlipIncluded === null) return 'Please indicate if packing slip is included';
        if (formData.orderQuantityComplete === null) return 'Please indicate if order quantity is complete';
        if (formData.labelingAccurate === null) return 'Please indicate if labeling is accurate';
        if (formData.shelfLifeAcceptable === null) return 'Please indicate if shelf life is acceptable';
        return null;
      case 2:
        if (!formData.storageConfirmed && !formData.storageOverride) return 'Please confirm storage or provide override';
        if (formData.storageOverride && !formData.storageOverrideReason) return 'Override reason is required';
        return null;
      default:
        return null;
    }
  };

  const handleSaveProgress = async () => {
    try {
      setSaving(true);
      setError('');

      const payload = {
        ...formData,
        costPerItem: parseFloat(formData.costPerItem),
        qtyReceived: parseInt(formData.qtyReceived),
        expirationDate: formData.expirationDateNA ? null : formData.expirationDate,
        lotNumber: formData.lotNumberNA ? null : formData.lotNumber,
      };

      let currentReceivingId = receivingId;
      
      if (currentReceivingId) {
        await rawMaterialsAPI.updateReceiving(currentReceivingId, payload);
      } else {
        const response = await rawMaterialsAPI.createReceiving({
          ...payload,
          rawMaterialItemId: item.id,
        });
        if (response.success) {
          currentReceivingId = response.data.id;
          setReceivingId(currentReceivingId);
        }
      }
      
      // Upload packing slip files if any are pending
      if (packingSlipFiles.length > 0 && currentReceivingId) {
        try {
          const uploadResult = await rawMaterialsAPI.uploadPackingSlips(currentReceivingId, packingSlipFiles);
          if (uploadResult.success) {
            setPackingSlipFiles([]); // Clear pending files
            setUploadedPackingSlips(uploadResult.data.allFiles || []);
        }
        } catch (uploadErr) {
          console.error('Failed to upload packing slips:', uploadErr);
          // Continue anyway, files can be uploaded later
        }
      }
      
      onSuccess('Receiving saved. You can continue later.');
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleFinishReceiving = async () => {
    // Validate all steps
    for (let i = 0; i <= 2; i++) {
      const err = validateStep(i);
      if (err) {
        setActiveStep(i);
        setError(err);
        return;
      }
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        ...formData,
        costPerItem: parseFloat(formData.costPerItem),
        qtyReceived: parseInt(formData.qtyReceived),
        expirationDate: formData.expirationDateNA ? null : formData.expirationDate,
        lotNumber: formData.lotNumberNA ? null : formData.lotNumber,
      };

      let currentReceivingId = receivingId;

      if (currentReceivingId) {
        await rawMaterialsAPI.updateReceiving(currentReceivingId, payload);
      } else {
        const response = await rawMaterialsAPI.createReceiving({
          ...payload,
          rawMaterialItemId: item.id,
        });
        if (response.success) {
          currentReceivingId = response.data.id;
        }
      }
      
      // Upload packing slip files if any are pending
      if (packingSlipFiles.length > 0 && currentReceivingId) {
        try {
          await rawMaterialsAPI.uploadPackingSlips(currentReceivingId, packingSlipFiles);
          setPackingSlipFiles([]); // Clear pending files
        } catch (uploadErr) {
          console.error('Failed to upload packing slips:', uploadErr);
          // Continue anyway, files can be uploaded in QA review
        }
      }

      // Complete the receiving
      const completeResponse = await rawMaterialsAPI.completeReceiving(currentReceivingId);
      onSuccess(completeResponse.message || 'Receiving completed. Ready for QA Review.');
    } catch (err) {
      setError(err.message || 'Failed to complete receiving');
    } finally {
      setSaving(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#039582', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>Receive Supplies</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{item.itemDescription}</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step descriptions */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {activeStep === 0 && "📦 Enter basic receiving information: cost, vendor, dates, lot number, and quantity."}
            {activeStep === 1 && "✅ Complete the inspection checklist: verify packing slip, order completeness, labeling, and shelf life."}
            {activeStep === 2 && "❄️ Confirm storage location matches requirements, or provide override reason if different."}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Step 0: Basic Info */}
        {activeStep === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Cost Per Item"
                type="number"
                value={formData.costPerItem}
                onChange={(e) => handleChange('costPerItem', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vendor / Manufacturer"
                value={formData.vendor}
                onChange={(e) => handleChange('vendor', e.target.value)}
                helperText="Pre-filled from item master, can be edited if different"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PO Number"
                value={formData.poNumber}
                onChange={(e) => handleChange('poNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Date Received"
                type="date"
                value={formData.dateReceived}
                onChange={(e) => handleChange('dateReceived', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required={!formData.lotNumberNA}
                disabled={formData.lotNumberNA}
                label="Lot Number"
                value={formData.lotNumber}
                onChange={(e) => handleChange('lotNumber', e.target.value)}
              />
              <FormControlLabel
                control={<Checkbox checked={formData.lotNumberNA} onChange={(e) => handleChange('lotNumberNA', e.target.checked)} />}
                label="N/A"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required={!formData.expirationDateNA}
                disabled={formData.expirationDateNA}
                label="Expiration Date"
                type="date"
                value={formData.expirationDate}
                onChange={(e) => handleChange('expirationDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <FormControlLabel
                control={<Checkbox checked={formData.expirationDateNA} onChange={(e) => handleChange('expirationDateNA', e.target.checked)} />}
                label="N/A"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Quantity Received"
                type="number"
                value={formData.qtyReceived}
                onChange={(e) => handleChange('qtyReceived', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Container Condition</InputLabel>
                <Select
                  value={formData.containerCondition}
                  label="Container Condition"
                  onChange={(e) => handleChange('containerCondition', e.target.value)}
                >
                  <MenuItem value="ACCEPTABLE">Acceptable</MenuItem>
                  <MenuItem value="UNACCEPTABLE">Unacceptable</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.containerCondition === 'UNACCEPTABLE' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Container Condition Notes"
                  value={formData.containerConditionNotes}
                  onChange={(e) => handleChange('containerConditionNotes', e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Step 1: Inspection Checklist */}
        {activeStep === 1 && (
          <Box>
            {/* Packing Slip Question with File Upload */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: formData.packingSlipIncluded === false ? '#fef2f2' : '#f8fafc' }}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend" sx={{ fontWeight: 600 }}>Is Packing Slip Included?</FormLabel>
                <RadioGroup
                  row
                  value={formData.packingSlipIncluded === null ? '' : formData.packingSlipIncluded.toString()}
                  onChange={(e) => handleChange('packingSlipIncluded', e.target.value === 'true')}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Yes" />
                  <FormControlLabel value="false" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
              {formData.packingSlipIncluded === true && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Upload packing slip(s) - you can select multiple files:
                  </Typography>
                  <input
                    ref={packingSlipInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    capture="environment"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        setPackingSlipFiles(prev => [...prev, ...files]);
                        handleChange('packingSlipNotes', 
                          (formData.packingSlipNotes ? formData.packingSlipNotes + '\n' : '') + 
                          `Files selected: ${files.map(f => f.name).join(', ')}`
                        );
                      }
                      if (packingSlipInputRef.current) {
                        packingSlipInputRef.current.value = '';
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => packingSlipInputRef.current?.click()}
                    >
                      Add Packing Slip(s)
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      Files will be uploaded when you save
                    </Typography>
                  </Box>
                  
                  {/* Show uploaded files from Azure */}
                  {uploadedPackingSlips.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" fontWeight={600} color="success.main">
                        Uploaded Files:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {uploadedPackingSlips.map((file, idx) => (
                    <Chip 
                            key={`uploaded-${idx}`}
                            icon={<ViewIcon sx={{ fontSize: 14 }} />}
                            label={file.name || `Packing Slip ${idx + 1}`}
                            size="small"
                      color="success" 
                            onClick={() => window.open(file.url, '_blank', 'noopener,noreferrer')}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  {/* Show pending files to upload */}
                  {packingSlipFiles.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" fontWeight={600} color="warning.main">
                        Pending Upload ({packingSlipFiles.length} files):
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {packingSlipFiles.map((file, idx) => (
                          <Chip
                            key={`pending-${idx}`}
                            label={file.name}
                      size="small" 
                            color="warning"
                            onDelete={() => setPackingSlipFiles(prev => prev.filter((_, i) => i !== idx))}
                    />
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Notes"
                    value={formData.packingSlipNotes}
                    onChange={(e) => handleChange('packingSlipNotes', e.target.value)}
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
              {formData.packingSlipIncluded === false && (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Notes / Explanation"
                  value={formData.packingSlipNotes}
                  onChange={(e) => handleChange('packingSlipNotes', e.target.value)}
                  sx={{ mt: 1 }}
                />
              )}
            </Paper>

            {/* Other Checklist Questions */}
            {[
              { field: 'orderQuantityComplete', label: 'Is Order Quantity Complete?', notesField: 'orderQuantityNotes' },
              { field: 'labelingAccurate', label: 'Is Labeling Accurate?', notesField: 'labelingNotes' },
              { field: 'shelfLifeAcceptable', label: 'Is Shelf Life Acceptable?', notesField: 'shelfLifeNotes' },
            ].map((item, idx) => (
              <Paper key={idx} sx={{ p: 2, mb: 2, bgcolor: formData[item.field] === false ? '#fef2f2' : '#f8fafc' }}>
                <FormControl component="fieldset" required>
                  <FormLabel component="legend" sx={{ fontWeight: 600 }}>{item.label}</FormLabel>
                  <RadioGroup
                    row
                    value={formData[item.field] === null ? '' : formData[item.field].toString()}
                    onChange={(e) => handleChange(item.field, e.target.value === 'true')}
                  >
                    <FormControlLabel value="true" control={<Radio />} label="Yes" />
                    <FormControlLabel value="false" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
                {formData[item.field] === false && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Notes / Explanation"
                    value={formData[item.notesField]}
                    onChange={(e) => handleChange(item.notesField, e.target.value)}
                    sx={{ mt: 1 }}
                  />
                )}
              </Paper>
            ))}
          </Box>
        )}

        {/* Step 2: Storage Confirmation */}
        {activeStep === 2 && (
          <Box>
            <Paper sx={{ p: 2.5, mb: 2, bgcolor: '#f0fdfa', border: '1px solid #b2f5ea' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#1a5f5a' }}>
                Required Storage Conditions
              </Typography>
              
              {/* Display each storage type with icon and notes */}
              {(() => {
                const storageTypes = [];
                if (item.storageAmbient) storageTypes.push({ type: 'Ambient', icon: <AmbientIcon sx={{ fontSize: 20 }} /> });
                if (item.storageFrozen) storageTypes.push({ type: 'Frozen', icon: <FrozenIcon sx={{ fontSize: 20 }} /> });
                if (item.storageRefrigerated) storageTypes.push({ type: 'Refrigerated', icon: <RefrigeratedIcon sx={{ fontSize: 20 }} /> });
                
                if (storageTypes.length === 0) {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Not specified
                    </Typography>
                  );
                }
                
                return (
                  <Stack spacing={2}>
                    {storageTypes.map((storage, index) => (
                      <Box key={index}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {storage.icon}
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a5f5a' }}>
                            {storage.type}
                          </Typography>
                          {item.storageTemperatureRange && index === 0 && (
                            <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                              ({item.storageTemperatureRange})
                            </Typography>
                          )}
                        </Stack>
                        {/* Show storage notes underneath the first storage type (or all if only one) */}
                        {item.storageNotes && (storageTypes.length === 1 || index === 0) && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary', 
                              display: 'block',
                              mt: 0.5,
                              ml: 4,
                              fontSize: '0.85rem',
                              lineHeight: 1.5
                            }}
                          >
                            {item.storageNotes}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                );
              })()}
            </Paper>

            <FormControlLabel
              control={<Checkbox checked={formData.storageConfirmed} onChange={(e) => handleChange('storageConfirmed', e.target.checked)} disabled={formData.storageOverride} />}
              label="I confirm the material has been stored according to requirements"
            />

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={<Checkbox checked={formData.storageOverride} onChange={(e) => handleChange('storageOverride', e.target.checked)} />}
              label="Override storage requirements (one-time)"
            />

            {formData.storageOverride && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Actual Storage Location/Condition"
                    value={formData.storageOverrideValue}
                    onChange={(e) => handleChange('storageOverrideValue', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={2}
                    label="Override Reason (Required)"
                    value={formData.storageOverrideReason}
                    onChange={(e) => handleChange('storageOverrideReason', e.target.value)}
                  />
                </Grid>
              </Grid>
            )}

            {item.certificateRequired && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Certificate Required:</strong> Yes (COA / COC / COS) - A quality certificate is required for this item. Please request from vendor if not already received.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>
          <Tooltip title="Save your progress and close. You can continue later by clicking 'Continue' on the receiving record.">
            <Button onClick={handleSaveProgress} disabled={saving} startIcon={<SaveIcon />}>
              Save & Close
            </Button>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleBack} disabled={activeStep === 0 || saving}>
            Back
          </Button>
          {activeStep < steps.length - 1 ? (
            <Tooltip title="Continue to next step. Make sure all required fields are filled.">
              <Button 
                variant="contained" 
                onClick={() => {
                  const err = validateStep(activeStep);
                  if (err) {
                    setError(err);
                  } else {
                    setError('');
                    handleNext();
                  }
                }} 
                sx={{ bgcolor: '#039582' }}
              >
                Next
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Complete the receiving process. This will move the items to QA Review status.">
              <Button
                variant="contained"
                onClick={handleFinishReceiving}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <CheckIcon />}
                sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
              >
                Finish Receiving
              </Button>
            </Tooltip>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// QA REVIEW DIALOG
// ============================================

const QAReviewDialog = ({ open, onClose, receiving, onSuccess }) => {
  const [formData, setFormData] = useState({
    certificateSatisfactory: '',
    certificateFileUrl: '',
    certificateFileName: '',
    qtyConforming: '',
    qtyNonconforming: '0',
    qaNotes: '',
  });
  
  // Editable receiving fields
  const [editableFields, setEditableFields] = useState({
    costPerItem: '',
    vendor: '',
    poNumber: '',
    lotNumber: '',
    lotNumberNA: false,
    expirationDate: '',
    expirationDateNA: false,
    qtyReceived: '',
    containerCondition: '',
    containerConditionNotes: '',
    packingSlipIncluded: null,
    packingSlipNotes: '',
    orderQuantityComplete: null,
    orderQuantityNotes: '',
    labelingAccurate: null,
    labelingNotes: '',
    shelfLifeAcceptable: null,
    shelfLifeNotes: '',
  });
  
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');
  const [editedFields, setEditedFields] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [packingSlipFiles, setPackingSlipFiles] = useState([]); // Support multiple files
  const packingSlipInputRef = React.useRef(null);

  useEffect(() => {
    if (receiving) {
      setFormData({
        certificateSatisfactory: receiving.certificateSatisfactory || '',
        certificateFileUrl: receiving.certificateFileUrl || '',
        certificateFileName: receiving.certificateFileUrl ? 'Certificate uploaded' : '',
        qtyConforming: receiving.qtyConforming?.toString() || receiving.qtyReceived?.toString() || '',
        qtyNonconforming: receiving.qtyNonconforming?.toString() || '0',
        qaNotes: receiving.qaNotes || '',
      });
      setEditableFields({
        costPerItem: receiving.costPerItem?.toString() || '',
        vendor: receiving.vendor || '',
        poNumber: receiving.poNumber || '',
        lotNumber: receiving.lotNumber || '',
        lotNumberNA: receiving.lotNumberNA || false,
        expirationDate: formatDateForInput(receiving.expirationDate),
        expirationDateNA: receiving.expirationDateNA || false,
        qtyReceived: receiving.qtyReceived?.toString() || '',
        containerCondition: receiving.containerCondition || 'ACCEPTABLE',
        containerConditionNotes: receiving.containerConditionNotes || '',
        packingSlipIncluded: receiving.packingSlipIncluded,
        packingSlipNotes: receiving.packingSlipNotes || '',
        orderQuantityComplete: receiving.orderQuantityComplete,
        orderQuantityNotes: receiving.orderQuantityNotes || '',
        labelingAccurate: receiving.labelingAccurate,
        labelingNotes: receiving.labelingNotes || '',
        shelfLifeAcceptable: receiving.shelfLifeAcceptable,
        shelfLifeNotes: receiving.shelfLifeNotes || '',
      });
      // Parse existing packing slip files
      if (receiving.packingSlipFileUrl) {
        try {
          const parsed = JSON.parse(receiving.packingSlipFileUrl);
          setPackingSlipFiles(Array.isArray(parsed) ? parsed : [{ url: receiving.packingSlipFileUrl, name: 'Packing Slip' }]);
        } catch {
          setPackingSlipFiles(receiving.packingSlipFileUrl ? [{ url: receiving.packingSlipFileUrl, name: 'Packing Slip' }] : []);
        }
      } else {
        setPackingSlipFiles([]);
      }
      setError('');
      setEditedFields({});
      setIsEditing(false);
    }
  }, [receiving, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEditableFieldChange = (field, value) => {
    const originalValue = receiving[field];
    setEditableFields(prev => ({ ...prev, [field]: value }));
    
    // Track changes for audit
    if (value !== originalValue && value !== originalValue?.toString()) {
      setEditedFields(prev => ({ ...prev, [field]: { oldValue: originalValue, newValue: value } }));
    } else {
      // Remove from edited if reverted to original
      setEditedFields(prev => {
        const newEdited = { ...prev };
        delete newEdited[field];
        return newEdited;
      });
    }
  };

  const [savingEdits, setSavingEdits] = useState(false);
  
  // Save edits immediately to database
  const handleSaveEdits = async () => {
    if (Object.keys(editedFields).length === 0) {
      setIsEditing(false);
      return;
    }
    
    try {
      setSavingEdits(true);
      setError('');
      
      // Only send the fields that were actually edited
      const updates = {};
      
      // Cost per item - required field, must be a number
      if (editedFields.costPerItem !== undefined) {
        const cost = parseFloat(editableFields.costPerItem);
        if (isNaN(cost) || cost < 0) {
          setError('Cost per item must be a valid positive number');
          setSavingEdits(false);
          return;
        }
        updates.costPerItem = cost;
      }
      
      // Qty received - required field, must be a positive integer
      if (editedFields.qtyReceived !== undefined) {
        const qty = parseInt(editableFields.qtyReceived);
        if (isNaN(qty) || qty < 1) {
          setError('Quantity received must be at least 1');
          setSavingEdits(false);
          return;
        }
        updates.qtyReceived = qty;
      }
      
      // Optional string fields
      if (editedFields.vendor !== undefined) {
        updates.vendor = editableFields.vendor || null;
      }
      if (editedFields.poNumber !== undefined) {
        updates.poNumber = editableFields.poNumber || null;
      }
      if (editedFields.lotNumber !== undefined) {
        updates.lotNumber = editableFields.lotNumberNA ? null : (editableFields.lotNumber || null);
      }
      if (editedFields.lotNumberNA !== undefined) {
        updates.lotNumberNA = editableFields.lotNumberNA;
      }
      if (editedFields.containerCondition !== undefined) {
        updates.containerCondition = editableFields.containerCondition || 'ACCEPTABLE';
      }
      if (editedFields.containerConditionNotes !== undefined) {
        updates.containerConditionNotes = editableFields.containerConditionNotes || null;
      }
      
      // Expiration date - needs to be ISO string or null
      // Add time component to avoid timezone issues (noon CST)
      if (editedFields.expirationDate !== undefined || editedFields.expirationDateNA !== undefined) {
        updates.expirationDateNA = editableFields.expirationDateNA;
        if (editableFields.expirationDateNA) {
          updates.expirationDate = null;
        } else if (editableFields.expirationDate) {
          // Add noon time to avoid date shifting due to timezone
          updates.expirationDate = new Date(editableFields.expirationDate + 'T12:00:00').toISOString();
        }
      }
      
      const response = await rawMaterialsAPI.updateQAReview(receiving.id, { 
        updates,
        editReason: 'QA Review field edits: ' + Object.keys(editedFields).join(', ')
      });
      
      if (response.success) {
        // Update the local receiving object with new values from response
        if (response.data) {
          Object.assign(receiving, response.data);
        } else {
          // Fallback: update with our local values
          Object.keys(updates).forEach(key => {
            receiving[key] = updates[key];
          });
        }
        
        // Also update qtyConforming to match new qtyReceived if it was the same before
        if (updates.qtyReceived) {
          const oldQtyStr = receiving.qtyReceived?.toString();
          if (formData.qtyConforming === oldQtyStr || !formData.qtyConforming) {
            setFormData(prev => ({ ...prev, qtyConforming: updates.qtyReceived.toString() }));
          }
        }
        
        // Clear edited fields and exit edit mode
        setEditedFields({});
        setIsEditing(false);
        
        // Show success feedback
        setError(''); // Clear any previous errors
      } else {
        setError(response.error || 'Failed to save edits');
      }
    } catch (err) {
      console.error('Save edits error:', err);
      setError(err.message || 'Failed to save edits. Please try again.');
    } finally {
      setSavingEdits(false);
    }
  };

  const handleReceivingFieldEdit = (field, value, originalValue) => {
    if (value !== originalValue) {
      setEditedFields(prev => ({ ...prev, [field]: { oldValue: originalValue, newValue: value } }));
    }
  };

  const [uploadingPackingSlips, setUploadingPackingSlips] = useState(false);
  
  const handlePackingSlipUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploadingPackingSlips(true);
    setError('');
    
    try {
      // Upload files to Azure
      const result = await rawMaterialsAPI.uploadPackingSlips(receiving.id, files);
      
      if (result.success) {
        // Update the local state with the uploaded files
        setPackingSlipFiles(result.data.allFiles || []);
        // Update notes about what was uploaded
        const currentNotes = editableFields?.packingSlipNotes || '';
        handleEditableFieldChange('packingSlipNotes', 
          (currentNotes ? currentNotes + '\n' : '') + 
          `Files uploaded: ${files.map(f => f.name).join(', ')}`
        );
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Packing slip upload error:', err);
      setError(`Failed to upload packing slips: ${err.message || 'Unknown error'}`);
    } finally {
      setUploadingPackingSlips(false);
      // Reset input
      if (packingSlipInputRef.current) {
        packingSlipInputRef.current.value = '';
      }
    }
  };

  const handleRemovePackingSlip = async (index) => {
    const fileToRemove = packingSlipFiles[index];
    if (!fileToRemove) return;
    
    try {
      // Delete from Azure
      await rawMaterialsAPI.deletePackingSlip(receiving.id, fileToRemove.url);
      setPackingSlipFiles(prev => prev.filter((_, i) => i !== index));
    } catch (err) {
      setError(`Failed to delete packing slip: ${err.message}`);
    }
  };

  const handleSaveProgress = async () => {
    try {
      setSaving(true);
      setError('');

      // Prepare packing slip file URLs (serialize as JSON array)
      const packingSlipFileUrl = packingSlipFiles.length > 0 
        ? JSON.stringify(packingSlipFiles.map(f => ({ url: f.url, name: f.name })))
        : null;

      const updates = {
        // QA Review fields
        certificateSatisfactory: formData.certificateSatisfactory || null,
        certificateFileUrl: formData.certificateFileUrl || null,
        qtyConforming: parseInt(formData.qtyConforming) || null,
        qtyNonconforming: parseInt(formData.qtyNonconforming) || null,
        qaNotes: formData.qaNotes || null,
        // Editable receiving fields (if editing mode)
        ...(isEditing && {
          costPerItem: parseFloat(editableFields.costPerItem) || null,
          vendor: editableFields.vendor || null,
          poNumber: editableFields.poNumber || null,
          lotNumber: editableFields.lotNumber || null,
          lotNumberNA: editableFields.lotNumberNA,
          expirationDate: editableFields.expirationDate || null,
          expirationDateNA: editableFields.expirationDateNA,
          qtyReceived: parseInt(editableFields.qtyReceived) || null,
          containerCondition: editableFields.containerCondition || null,
          containerConditionNotes: editableFields.containerConditionNotes || null,
          packingSlipIncluded: editableFields.packingSlipIncluded,
          packingSlipNotes: editableFields.packingSlipNotes || null,
          packingSlipFileUrl: packingSlipFileUrl,
          orderQuantityComplete: editableFields.orderQuantityComplete,
          orderQuantityNotes: editableFields.orderQuantityNotes || null,
          labelingAccurate: editableFields.labelingAccurate,
          labelingNotes: editableFields.labelingNotes || null,
          shelfLifeAcceptable: editableFields.shelfLifeAcceptable,
          shelfLifeNotes: editableFields.shelfLifeNotes || null,
        }),
      };

      const response = await rawMaterialsAPI.updateQAReview(receiving.id, { 
        updates,
        editReason: Object.keys(editedFields).length > 0 ? 'QA Review edits: ' + Object.keys(editedFields).join(', ') : undefined
      });

      if (response.success) {
        onSuccess('QA Review progress saved. You can continue later.');
      } else {
        setError(response.error || 'Failed to save changes');
      }
    } catch (err) {
      console.error('QA Review save error:', err);
      // Check if it's a session/auth error
      if (err.message?.includes('401') || err.message?.toLowerCase().includes('unauthorized')) {
        setError('Your session has expired. Please refresh the page and log in again.');
      } else if (err.message?.includes('status')) {
        setError('This item may have already been processed. Please close and refresh.');
      } else {
        setError(err.message || 'Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    const qtyConforming = parseInt(formData.qtyConforming) || 0;
    const qtyNonconforming = parseInt(formData.qtyNonconforming) || 0;
    const total = qtyConforming + qtyNonconforming;

    if (total !== receiving.qtyReceived) {
      setError(`Conforming (${qtyConforming}) + Nonconforming (${qtyNonconforming}) must equal Qty Received (${receiving.qtyReceived})`);
      return;
    }

    // Validate certificate if required
    const certificateRequired = receiving.rawMaterialItem?.certificateRequired;
    if (certificateRequired && !formData.certificateSatisfactory) {
      setError('Please indicate if the certificate is satisfactory');
      return;
    }
    if (certificateRequired && formData.certificateSatisfactory === 'YES' && !formData.certificateFileUrl) {
      setError('Please upload the quality certificate');
      return;
    }

    try {
      setCompleting(true);
      setError('');

      const response = await rawMaterialsAPI.completeQAReview(receiving.id, {
        certificateSatisfactory: formData.certificateSatisfactory || null,
        certificateFileUrl: formData.certificateFileUrl || null,
        qtyConforming,
        qtyNonconforming,
        qaNotes: formData.qaNotes,
      });

      onSuccess(response.message);
    } catch (err) {
      setError(err.message || 'Failed to complete QA review');
    } finally {
      setCompleting(false);
    }
  };

  if (!receiving) return null;

  // Get certificateRequired from rawMaterialItem, or fallback to checking if item has it
  const certificateRequired = receiving.rawMaterialItem?.certificateRequired === true || receiving.rawMaterialItem?.certificateRequired === 1;
  const isSaving = saving || completing;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: '#f97316', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>QA Review</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{receiving.itemDescription}</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Certificate Required Alert - Show prominently at top if required */}
        {certificateRequired && !formData.certificateSatisfactory && (
          <Alert severity="error" sx={{ mb: 3, border: '2px solid #ef4444' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              ⚠️ Certificate Status Required
            </Typography>
            <Typography variant="body2">
              This item requires a quality certificate ({receiving.rawMaterialItem?.certificateType || 'COA / COC / COS'}). 
              Please indicate if the certificate is satisfactory in the section below before completing QA Review.
            </Typography>
          </Alert>
        )}

        {/* Receiving Summary - All Fields Visible (Editable with Tracking) */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: isEditing ? '#fefce8' : '#f8fafc', border: isEditing ? '2px solid #f59e0b' : 'none' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1a5f5a' }}>
              Receiving Details {isEditing && '(Editing Mode - changes are tracked for GDP compliance)'}
          </Typography>
            {isEditing ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    // Cancel editing - reset to original values
                    setEditableFields({
                      costPerItem: receiving.costPerItem?.toString() || '',
                      vendor: receiving.vendor || '',
                      poNumber: receiving.poNumber || '',
                      lotNumber: receiving.lotNumber || '',
                      lotNumberNA: receiving.lotNumberNA || false,
                      expirationDate: formatDateForInput(receiving.expirationDate),
                      expirationDateNA: receiving.expirationDateNA || false,
                      qtyReceived: receiving.qtyReceived?.toString() || '',
                      containerCondition: receiving.containerCondition || 'ACCEPTABLE',
                      containerConditionNotes: receiving.containerConditionNotes || '',
                    });
                    setEditedFields({});
                    setIsEditing(false);
                  }}
                  disabled={savingEdits}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={handleSaveEdits}
                  disabled={savingEdits || Object.keys(editedFields).length === 0}
                  startIcon={savingEdits ? <CircularProgress size={16} /> : <SaveIcon />}
                >
                  {savingEdits ? 'Saving...' : 'Save Edits'}
                </Button>
              </Box>
            ) : (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => setIsEditing(true)}
                startIcon={<EditIcon />}
              >
                Edit Fields
              </Button>
            )}
          </Box>
          
          {Object.keys(editedFields).length > 0 && isEditing && (
            <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
              <Typography variant="caption">
                <strong>Unsaved Changes:</strong> {Object.keys(editedFields).join(', ')} - click "Save Edits" to apply
              </Typography>
            </Alert>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Date Received</Typography>
              <Typography variant="body2" fontWeight={500}>{formatDate(receiving.dateReceived)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Qty Received"
                  type="number"
                  value={editableFields.qtyReceived}
                  onChange={(e) => handleEditableFieldChange('qtyReceived', e.target.value)}
                  InputProps={{ inputProps: { min: 1 } }}
                />
              ) : (
                <>
              <Typography variant="caption" color="text.secondary">Qty Received</Typography>
              <Typography variant="body2" fontWeight={500}>{receiving.qtyReceived}</Typography>
                </>
              )}
            </Grid>
            <Grid item xs={6} sm={3}>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Lot Number"
                  value={editableFields.lotNumber}
                  onChange={(e) => handleEditableFieldChange('lotNumber', e.target.value)}
                  disabled={editableFields.lotNumberNA}
                />
              ) : (
                <>
              <Typography variant="caption" color="text.secondary">Lot Number</Typography>
              <Typography variant="body2" fontWeight={500}>{receiving.lotNumberNA ? 'N/A' : receiving.lotNumber}</Typography>
                </>
              )}
            </Grid>
            <Grid item xs={6} sm={3}>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Expiration Date"
                  type="date"
                  value={editableFields.expirationDate}
                  onChange={(e) => handleEditableFieldChange('expirationDate', e.target.value)}
                  disabled={editableFields.expirationDateNA}
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <>
              <Typography variant="caption" color="text.secondary">Expiration</Typography>
              <Typography variant="body2" fontWeight={500}>{receiving.expirationDateNA ? 'N/A' : (receiving.expirationDate ? formatDate(receiving.expirationDate) : 'N/A')}</Typography>
                </>
              )}
            </Grid>
            <Grid item xs={6} sm={3}>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Cost/Item"
                  type="number"
                  value={editableFields.costPerItem}
                  onChange={(e) => handleEditableFieldChange('costPerItem', e.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              ) : (
                <>
              <Typography variant="caption" color="text.secondary">Cost/Item</Typography>
              <Typography variant="body2" fontWeight={500}>${receiving.costPerItem}</Typography>
                </>
              )}
            </Grid>
            <Grid item xs={6} sm={3}>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Vendor"
                  value={editableFields.vendor}
                  onChange={(e) => handleEditableFieldChange('vendor', e.target.value)}
                />
              ) : (
                <>
              <Typography variant="caption" color="text.secondary">Vendor</Typography>
              <Typography variant="body2" fontWeight={500}>{receiving.vendor || '-'}</Typography>
                </>
              )}
            </Grid>
            <Grid item xs={6} sm={3}>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  label="PO Number"
                  value={editableFields.poNumber}
                  onChange={(e) => handleEditableFieldChange('poNumber', e.target.value)}
                />
              ) : (
                <>
              <Typography variant="caption" color="text.secondary">PO Number</Typography>
              <Typography variant="body2" fontWeight={500}>{receiving.poNumber || '-'}</Typography>
                </>
              )}
            </Grid>
            <Grid item xs={6} sm={3}>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Container Condition</InputLabel>
                  <Select
                    value={editableFields.containerCondition}
                    onChange={(e) => handleEditableFieldChange('containerCondition', e.target.value)}
                    label="Container Condition"
                  >
                    <MenuItem value="ACCEPTABLE">ACCEPTABLE</MenuItem>
                    <MenuItem value="UNACCEPTABLE">UNACCEPTABLE</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <>
              <Typography variant="caption" color="text.secondary">Container Condition</Typography>
              <Chip 
                label={receiving.containerCondition} 
                size="small" 
                color={receiving.containerCondition === 'ACCEPTABLE' ? 'success' : 'error'} 
              />
                </>
              )}
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="caption" fontWeight={600} color="text.secondary">Checklist Results:</Typography>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption">
                  Packing Slip: {editableFields.packingSlipIncluded ? '✓ Yes' : '✗ No'}
                </Typography>
                {/* View Packing Slip Button */}
                {packingSlipFiles.length > 0 && (
                  <Tooltip title="View uploaded packing slips">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => {
                        packingSlipFiles.forEach(f => window.open(f.url, '_blank', 'noopener,noreferrer'));
                      }}
                    >
                      <ViewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption">Order Complete: {editableFields.orderQuantityComplete ? '✓ Yes' : '✗ No'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption">Labeling Accurate: {editableFields.labelingAccurate ? '✓ Yes' : '✗ No'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption">Shelf Life OK: {editableFields.shelfLifeAcceptable ? '✓ Yes' : '✗ No'}</Typography>
            </Grid>
          </Grid>
          
          {/* Multiple Packing Slips Upload Section - Always visible in QA Review */}
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f9ff', borderRadius: 1, border: '1px solid #0ea5e9' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#0369a1' }}>
              📎 Packing Slips ({packingSlipFiles.length} uploaded)
            </Typography>
            
            {/* Always allow uploading packing slips in QA Review */}
            <input
              ref={packingSlipInputRef}
              type="file"
              accept="image/*,.pdf"
              multiple
              style={{ display: 'none' }}
              onChange={handlePackingSlipUpload}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => packingSlipInputRef.current?.click()}
                disabled={uploadingPackingSlips}
                startIcon={uploadingPackingSlips ? <CircularProgress size={16} /> : null}
              >
                {uploadingPackingSlips ? 'Uploading...' : 'Add Packing Slip(s)'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Upload or re-upload packing slips to Azure storage
              </Typography>
            </Box>
            
            {packingSlipFiles.length > 0 ? (
              <Box>
                {/* Warn if any files have temporary/invalid URLs */}
                {packingSlipFiles.some(f => f.url?.startsWith('blob:') || !f.url?.startsWith('http')) && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      ⚠️ Some packing slips have temporary URLs that won't work. Please delete them and re-upload.
                    </Typography>
                  </Alert>
                )}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {packingSlipFiles.map((file, idx) => {
                    const isTemporaryUrl = file.url?.startsWith('blob:') || !file.url?.startsWith('http');
                    return (
                      <Chip
                        key={idx}
                        icon={<ViewIcon sx={{ fontSize: 16 }} />}
                        label={file.name || `Packing Slip ${idx + 1}`}
                        size="small"
                        color={isTemporaryUrl ? 'error' : 'success'}
                        onClick={() => {
                          if (isTemporaryUrl) {
                            alert('This file has a temporary URL that cannot be viewed. Please delete and re-upload.');
                          } else {
                            window.open(file.url, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        onDelete={() => handleRemovePackingSlip(idx)}
                        sx={{ cursor: 'pointer' }}
                      />
                    );
                  })}
                </Box>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  No packing slips uploaded. If you have packing slips, please upload them now.
                </Typography>
              </Alert>
            )}
          </Box>
          
          {receiving.storageOverride && (
            <Alert severity="info" sx={{ mt: 2 }} icon={false}>
              <Typography variant="caption">
                <strong>Storage Override:</strong> {receiving.storageOverrideValue} - {receiving.storageOverrideReason}
              </Typography>
            </Alert>
          )}
        </Paper>

        {/* Certificate Section - Only show if certificateRequired = true */}
        {certificateRequired && (
          <Paper sx={{ p: 2, mb: 3, border: formData.certificateSatisfactory ? '2px solid #22c55e' : '2px solid #f97316', bgcolor: formData.certificateSatisfactory ? '#f0fdf4' : '#fffbeb' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: '#f97316', display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon sx={{ fontSize: 20 }} /> Quality Certificate Required (COA / COC / COS)
            </Typography>
            <FormControl component="fieldset" sx={{ mb: 1.5, width: '100%' }} required>
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, fontSize: '0.95rem' }}>
                Is the certificate satisfactory? *
              </FormLabel>
              <RadioGroup
                row
                value={formData.certificateSatisfactory}
                onChange={(e) => {
                  handleChange('certificateSatisfactory', e.target.value);
                  setError(''); // Clear error when user makes selection
                }}
                sx={{ gap: 2 }}
              >
                <FormControlLabel 
                  value="YES" 
                  control={<Radio color="success" />} 
                  label="Yes - Certificate is satisfactory" 
                  sx={{ 
                    border: formData.certificateSatisfactory === 'YES' ? '2px solid #22c55e' : '1px solid #e5e7eb',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    bgcolor: formData.certificateSatisfactory === 'YES' ? '#dcfce7' : 'transparent'
                  }}
                />
                <FormControlLabel 
                  value="NO" 
                  control={<Radio color="error" />} 
                  label="No - Certificate is not satisfactory" 
                  sx={{ 
                    border: formData.certificateSatisfactory === 'NO' ? '2px solid #ef4444' : '1px solid #e5e7eb',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    bgcolor: formData.certificateSatisfactory === 'NO' ? '#fee2e2' : 'transparent'
                  }}
                />
                <FormControlLabel 
                  value="NA" 
                  control={<Radio />} 
                  label="N/A - Not applicable" 
                  sx={{ 
                    border: formData.certificateSatisfactory === 'NA' ? '2px solid #6b7280' : '1px solid #e5e7eb',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    bgcolor: formData.certificateSatisfactory === 'NA' ? '#f3f4f6' : 'transparent'
                  }}
                />
              </RadioGroup>
              {!formData.certificateSatisfactory && (
                <FormHelperText error sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                  * You must select Yes, No, or N/A to complete QA Review
                </FormHelperText>
              )}
              {!formData.certificateSatisfactory && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: '0.8rem' }}>
                  Note: If you select "Yes", you will be required to upload the quality certificate file.
                </Typography>
              )}
            </FormControl>
            
            {formData.certificateSatisfactory === 'YES' && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f0fdf4', border: '2px solid #22c55e', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: '#16a34a' }}>
                  📄 Upload Quality Certificate (Required)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                  Upload quality certificate file (COA, COC, or COS). Accepts images or PDF files.
                </Typography>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  capture="environment"
                  id="certificate-upload"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // For now, store filename - in production this would upload to Azure Blob
                      handleChange('certificateFileName', file.name);
                      handleChange('certificateFileUrl', URL.createObjectURL(file));
                      setError(''); // Clear any errors
                    }
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <label htmlFor="certificate-upload">
                    <Button
                      variant="contained"
                      component="span"
                      color="success"
                      startIcon={<SaveIcon />}
                      sx={{ 
                        bgcolor: '#22c55e',
                        '&:hover': { bgcolor: '#16a34a' }
                      }}
                    >
                      {formData.certificateFileUrl ? 'Replace Certificate' : 'Upload Certificate'}
                    </Button>
                  </label>
                  {formData.certificateFileUrl && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        icon={<CheckIcon />}
                        label={formData.certificateFileName || 'Certificate uploaded'} 
                        color="success"
                        onDelete={() => {
                          handleChange('certificateFileUrl', '');
                          handleChange('certificateFileName', '');
                        }}
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                        ✓ Certificate ready
                      </Typography>
                    </Box>
                  )}
                  {!formData.certificateFileUrl && (
                    <Typography variant="caption" color="error.main" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      ⚠️ Certificate upload required
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            
            {formData.certificateSatisfactory === 'NO' && !receiving.certificateOverridden && (
              <Alert severity="error" sx={{ mt: 1.5, py: 1 }}>
                <Typography variant="body2">
                  Certificate is not satisfactory. Contact an Admin for override or reject the items.
                </Typography>
              </Alert>
            )}
            
            {receiving.certificateOverridden && (
              <Alert severity="warning" sx={{ mt: 1.5, py: 1 }}>
                <Typography variant="body2">
                  <strong>Admin Override Applied:</strong> By {receiving.certificateOverrideByName} - {receiving.certificateOverrideReason}
                </Typography>
              </Alert>
            )}
          </Paper>
        )}

        {/* Quantity Review */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#1a5f5a' }}>Quantity Review</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              label="Qty Conforming (Accepted)"
              type="number"
              value={formData.qtyConforming}
              onChange={(e) => handleChange('qtyConforming', e.target.value)}
              InputProps={{ inputProps: { min: 0, max: receiving.qtyReceived } }}
              helperText="Units that passed QA"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Qty Nonconforming (Rejected)"
              type="number"
              value={formData.qtyNonconforming}
              onChange={(e) => handleChange('qtyNonconforming', e.target.value)}
              InputProps={{ inputProps: { min: 0, max: receiving.qtyReceived } }}
              helperText="Units that failed QA"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, bgcolor: '#f3f4f6', textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary">Total Must Equal</Typography>
              <Typography variant="h4" fontWeight={700} color={
                (parseInt(formData.qtyConforming) || 0) + (parseInt(formData.qtyNonconforming) || 0) === receiving.qtyReceived 
                  ? '#22c55e' : '#ef4444'
              }>
                {receiving.qtyReceived}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Current: {(parseInt(formData.qtyConforming) || 0) + (parseInt(formData.qtyNonconforming) || 0)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* QA Notes */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="QA Review Notes"
          value={formData.qaNotes}
          onChange={(e) => handleChange('qaNotes', e.target.value)}
          placeholder="Document any observations, issues, or notes from the QA review..."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleSaveProgress}
            disabled={isSaving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Progress'}
          </Button>
          <Button
            variant="contained"
            onClick={handleComplete}
            disabled={isSaving}
            startIcon={completing ? <CircularProgress size={20} /> : <CheckIcon />}
            sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
          >
            {completing ? 'Finishing...' : 'Finish QA Review'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// VIEW APPROVED DIALOG
// ============================================

const ADJUSTMENT_REASONS = [
  { value: 'R&D', label: 'Research & Development', icon: '🔬' },
  { value: 'CLEANING', label: 'Cleaning / Sanitation', icon: '🧹' },
  { value: 'TRAINING', label: 'Training', icon: '📚' },
  { value: 'DAMAGED', label: 'Damaged', icon: '💔' },
  { value: 'EXPIRED', label: 'Expired', icon: '⏰' },
  { value: 'LOST', label: 'Lost / Missing', icon: '❓' },
  { value: 'QUALITY_ISSUE', label: 'Quality Issue', icon: '⚠️' },
  { value: 'OTHER', label: 'Other', icon: '📝' },
];

const ViewApprovedDialog = ({ open, onClose, receiving, onInventoryUpdated }) => {
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [availableQty, setAvailableQty] = useState(null);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [adjustError, setAdjustError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [adjustForm, setAdjustForm] = useState({
    quantity: '',
    reason: '',
    notes: '',
  });
  const [deleteForm, setDeleteForm] = useState({
    reason: '',
    notes: '',
  });
  
  // Packing slip management
  const [packingSlipFiles, setPackingSlipFiles] = useState([]);
  const [uploadingPackingSlips, setUploadingPackingSlips] = useState(false);
  const [packingSlipError, setPackingSlipError] = useState('');
  const packingSlipInputRef = React.useRef(null);
  
  // Load available quantity and adjustment history when dialog opens
  useEffect(() => {
    if (!open || !receiving?.id) return;
    
    let isMounted = true;
    
    const loadData = async () => {
      // Load all data in parallel for better performance
      const [, ,] = await Promise.all([
        isMounted ? loadAvailableQuantity() : Promise.resolve(),
        isMounted ? loadAdjustmentHistory() : Promise.resolve(),
      ]);
      if (isMounted) loadPackingSlips();
    };
    
    loadData();
    
    return () => { isMounted = false; };
  }, [open, receiving?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const loadPackingSlips = () => {
    if (!receiving?.packingSlipFileUrl) {
      setPackingSlipFiles([]);
      return;
    }
    try {
      const files = JSON.parse(receiving.packingSlipFileUrl);
      setPackingSlipFiles(Array.isArray(files) ? files : [{ name: 'Packing Slip', url: receiving.packingSlipFileUrl }]);
    } catch {
      setPackingSlipFiles(receiving.packingSlipFileUrl ? [{ name: 'Packing Slip', url: receiving.packingSlipFileUrl }] : []);
    }
  };
  
  const handlePackingSlipUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploadingPackingSlips(true);
    setPackingSlipError('');
    
    try {
      const result = await rawMaterialsAPI.uploadPackingSlips(receiving.id, files);
      if (result.success) {
        setPackingSlipFiles(result.data.allFiles || []);
        if (onInventoryUpdated) {
          onInventoryUpdated(`${files.length} packing slip(s) uploaded successfully`);
        }
      }
    } catch (err) {
      setPackingSlipError(`Failed to upload: ${err.message}`);
    } finally {
      setUploadingPackingSlips(false);
      if (packingSlipInputRef.current) {
        packingSlipInputRef.current.value = '';
      }
    }
  };
  
  const handleRemovePackingSlip = async (index) => {
    const fileToRemove = packingSlipFiles[index];
    if (!fileToRemove) return;
    
    try {
      await rawMaterialsAPI.deletePackingSlip(receiving.id, fileToRemove.url);
      setPackingSlipFiles(prev => prev.filter((_, i) => i !== index));
    } catch (err) {
      setPackingSlipError(`Failed to delete: ${err.message}`);
    }
  };
  
  const loadAvailableQuantity = async () => {
    if (!receiving?.id) return;
    setLoadingAvailable(true);
    try {
      const response = await rawMaterialsAPI.getAvailableQuantity(receiving.id);
      if (response.success) {
        setAvailableQty(response.data);
      }
    } catch (err) {
      console.error('Failed to load available quantity:', err);
    } finally {
      setLoadingAvailable(false);
    }
  };
  
  const loadAdjustmentHistory = async () => {
    if (!receiving?.id) return;
    try {
      const response = await rawMaterialsAPI.getAdjustmentHistory(receiving.id);
      if (response.success) {
        setAdjustmentHistory(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load adjustment history:', err);
    }
  };
  
  const handleAdjustSubmit = async () => {
    if (!adjustForm.quantity || parseInt(adjustForm.quantity) < 1) {
      setAdjustError('Please enter a valid quantity');
      return;
    }
    if (!adjustForm.reason) {
      setAdjustError('Please select a reason');
      return;
    }
    
    const qty = parseInt(adjustForm.quantity);
    if (availableQty && qty > availableQty.availableQty) {
      setAdjustError(`Cannot adjust ${qty} units. Only ${availableQty.availableQty} available.`);
      return;
    }
    
    setAdjusting(true);
    setAdjustError('');
    
    try {
      const response = await rawMaterialsAPI.adjustInventory(receiving.id, {
        quantity: qty,
        reason: adjustForm.reason,
        notes: adjustForm.notes || null,
      });
      
      if (response.success) {
        // Reset form and close dialog
        setAdjustForm({ quantity: '', reason: '', notes: '' });
        setAdjustDialogOpen(false);
        // Reload data
        loadAvailableQuantity();
        loadAdjustmentHistory();
        // Notify parent
        if (onInventoryUpdated) {
          onInventoryUpdated(response.message || 'Inventory adjusted successfully');
        }
      }
    } catch (err) {
      setAdjustError(err.message || 'Failed to adjust inventory');
    } finally {
      setAdjusting(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!deleteForm.reason) {
      setDeleteError('Please select a reason for deletion');
      return;
    }
    
    setDeleting(true);
    setDeleteError('');
    
    try {
      const response = await rawMaterialsAPI.deleteReceiving(
        receiving.id,
        deleteForm.reason,
        deleteForm.notes
      );
      
      if (response.success) {
        // Reset form and close dialogs
        setDeleteForm({ reason: '', notes: '' });
        setDeleteDialogOpen(false);
        onClose(); // Close main dialog
        // Notify parent
        if (onInventoryUpdated) {
          onInventoryUpdated(response.message || 'Item removed from inventory');
        }
      }
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };
  
  if (!receiving) return null;

  const certificateRequired = receiving.rawMaterialItem?.certificateRequired === true || receiving.rawMaterialItem?.certificateRequired === 1;
  const hasCertificate = receiving.certificateFileUrl || receiving.certificateSatisfactory;
  const currentAvailable = availableQty?.availableQty ?? (receiving.qtyConforming || 0);
  const totalAdjusted = availableQty?.totalAdjusted ?? 0;

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: '#22c55e', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>Approved Receiving Details</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{receiving.itemDescription}</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {/* Inventory Status Card */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#eff6ff', border: '2px solid #3b82f6' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1d4ed8' }}>
              📦 Inventory Status
            </Typography>
            <Button
              variant="contained"
              size="small"
              color="warning"
              startIcon={<EditIcon />}
              onClick={() => setAdjustDialogOpen(true)}
              sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
            >
              Adjust / Reconcile
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ ml: 1 }}
            >
              Remove from Inventory
            </Button>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#dcfce7' }}>
                <Typography variant="caption" color="text.secondary">Approved Qty</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#22c55e' }}>
                  {receiving.qtyConforming || 0}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#fef3c7' }}>
                <Typography variant="caption" color="text.secondary">Adjusted</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#f59e0b' }}>
                  {loadingAvailable ? '...' : totalAdjusted}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#dbeafe' }}>
                <Typography variant="caption" color="text.secondary">Available</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#3b82f6' }}>
                  {loadingAvailable ? '...' : currentAvailable}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Adjustment History */}
          {adjustmentHistory.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Recent Adjustments:
              </Typography>
              <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                {adjustmentHistory.slice(0, 5).map((adj, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #e5e7eb' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {ADJUSTMENT_REASONS.find(r => r.value === adj.reason)?.icon || '📝'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>-{adj.adjustmentQty}</strong> {ADJUSTMENT_REASONS.find(r => r.value === adj.reason)?.label || adj.reason}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {adj.adjustedBy} • {formatDate(adj.adjustedAt)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
        
        {/* Receiving Details */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#1a5f5a' }}>
            Receiving Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Date Received</Typography>
              <Typography variant="body2" fontWeight={500}>{formatDate(receiving.dateReceived)}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Qty Received</Typography>
              <Typography variant="body2" fontWeight={500}>{receiving.qtyReceived}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Qty Conforming (Approved)</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ color: '#22c55e' }}>{receiving.qtyConforming || 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Qty Nonconforming (Rejected)</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ color: '#ef4444' }}>{receiving.qtyNonconforming || 0}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Lot Number</Typography>
              <Typography variant="body2" fontWeight={500}>{receiving.lotNumberNA ? 'N/A' : receiving.lotNumber || '-'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Expiration</Typography>
              <Typography variant="body2" fontWeight={500}>{receiving.expirationDateNA ? 'N/A' : (receiving.expirationDate ? formatDate(receiving.expirationDate) : 'N/A')}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Cost/Item</Typography>
              <Typography variant="body2" fontWeight={500}>${receiving.costPerItem}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Vendor</Typography>
              <Typography variant="body2" fontWeight={500}>{receiving.vendor || '-'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">PO Number</Typography>
              <Typography variant="body2" fontWeight={500}>{receiving.poNumber || '-'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Container Condition</Typography>
              <Chip 
                label={receiving.containerCondition} 
                size="small" 
                color={receiving.containerCondition === 'ACCEPTABLE' ? 'success' : 'error'} 
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="caption" fontWeight={600} color="text.secondary">Checklist Results:</Typography>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption">Packing Slip: {receiving.packingSlipIncluded ? '✓ Yes' : '✗ No'}</Typography>
                {receiving.packingSlipFileUrl && (
                  <Tooltip title="View packing slip(s)">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => {
                        // Try to parse as JSON array
                        try {
                          const files = JSON.parse(receiving.packingSlipFileUrl);
                          if (Array.isArray(files)) {
                            files.forEach(f => window.open(f.url || f, '_blank', 'noopener,noreferrer'));
                          } else {
                            window.open(receiving.packingSlipFileUrl, '_blank', 'noopener,noreferrer');
                          }
                        } catch {
                          window.open(receiving.packingSlipFileUrl, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      <ViewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption">Order Complete: {receiving.orderQuantityComplete ? '✓ Yes' : '✗ No'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption">Labeling Accurate: {receiving.labelingAccurate ? '✓ Yes' : '✗ No'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption">Shelf Life OK: {receiving.shelfLifeAcceptable ? '✓ Yes' : '✗ No'}</Typography>
            </Grid>
          </Grid>
          
          {/* Packing slip count indicator - full management in section below */}
          {packingSlipFiles.length > 0 && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#0369a1' }}>
              📎 {packingSlipFiles.length} packing slip(s) uploaded - see Packing Slips section below to view/manage
            </Typography>
          )}
        </Paper>

        {/* Certificate Section */}
        {certificateRequired && hasCertificate && (
          <Paper sx={{ p: 2, mb: 3, border: '2px solid #22c55e', bgcolor: '#f0fdf4' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon /> Quality Certificate
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Certificate Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {receiving.certificateSatisfactory === 'YES' ? (
                    <Chip label="Satisfactory" color="success" size="small" />
                  ) : receiving.certificateSatisfactory === 'NO' ? (
                    <Chip label="Not Satisfactory" color="error" size="small" />
                  ) : receiving.certificateSatisfactory === 'NA' ? (
                    <Chip label="N/A" size="small" />
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                </Box>
              </Grid>
              {receiving.certificateFileUrl && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Certificate File</Typography>
                  <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={receiving.certificateFileName || 'Certificate uploaded'} 
                      color="success"
                      icon={<CheckIcon />}
                      sx={{ fontWeight: 600 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => window.open(receiving.certificateFileUrl, '_blank')}
                    >
                      View Certificate
                    </Button>
                  </Box>
                </Grid>
              )}
              {receiving.certificateOverridden && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Admin Override Applied:</strong> By {receiving.certificateOverrideByName} - {receiving.certificateOverrideReason}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* QA Review Notes */}
        {receiving.qaNotes && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#1a5f5a' }}>
              QA Review Notes
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {receiving.qaNotes}
            </Typography>
          </Paper>
        )}

        {/* Packing Slips Section - Always allow adding */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0369a1' }}>
              📎 Packing Slips ({packingSlipFiles.length} uploaded)
            </Typography>
          </Box>
          
          {packingSlipError && <Alert severity="error" sx={{ mb: 2 }}>{packingSlipError}</Alert>}
          
          {/* Upload button - always available */}
          <input
            ref={packingSlipInputRef}
            type="file"
            accept="image/*,.pdf"
            multiple
            style={{ display: 'none' }}
            onChange={handlePackingSlipUpload}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => packingSlipInputRef.current?.click()}
              disabled={uploadingPackingSlips}
              startIcon={uploadingPackingSlips ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {uploadingPackingSlips ? 'Uploading...' : 'Add Packing Slip(s)'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Upload additional packing slips at any time
            </Typography>
          </Box>
          
          {/* Display uploaded files */}
          {packingSlipFiles.length > 0 ? (
            <Box>
              {/* Warn if any files have temporary/invalid URLs */}
              {packingSlipFiles.some(f => f.url?.startsWith('blob:') || !f.url?.startsWith('http')) && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    ⚠️ Some packing slips have temporary URLs that won't work. Please delete and re-upload them.
                  </Typography>
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {packingSlipFiles.map((file, idx) => {
                  const isTemporaryUrl = file.url?.startsWith('blob:') || !file.url?.startsWith('http');
                  return (
                    <Chip
                      key={idx}
                      icon={<ViewIcon sx={{ fontSize: 16 }} />}
                      label={file.name || `Packing Slip ${idx + 1}`}
                      size="small"
                      color={isTemporaryUrl ? 'error' : 'success'}
                      onClick={() => {
                        if (isTemporaryUrl) {
                          alert('This file has a temporary URL that cannot be viewed. Please delete and re-upload.');
                        } else {
                          window.open(file.url, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      onDelete={() => handleRemovePackingSlip(idx)}
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No packing slips uploaded yet. Click "Add Packing Slip(s)" to upload.
            </Typography>
          )}
        </Paper>

        {/* Approval Information */}
        <Paper sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #22c55e' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#16a34a' }}>
            Approval Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Approved Quantity</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#22c55e' }}>
                {receiving.qtyConforming || 0} units
              </Typography>
            </Grid>
            {receiving.qaApprovedAt && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Approved Date</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDate(receiving.qaApprovedAt)}
                </Typography>
              </Grid>
            )}
            {receiving.qaApprovedBy && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Approved By</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {receiving.qaApprovedByName || receiving.qaApprovedBy}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Inventory Adjustment Dialog */}
    <Dialog open={adjustDialogOpen} onClose={() => setAdjustDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#f59e0b', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>📦 Adjust Inventory</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Remove items for non-donor usage
            </Typography>
          </Box>
          <IconButton onClick={() => setAdjustDialogOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {adjustError && <Alert severity="error" sx={{ mb: 2 }}>{adjustError}</Alert>}
        
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Use this to reconcile inventory for items used in R&D, cleaning, training, or other non-donor purposes. 
            This will be tracked in the audit log.
          </Typography>
        </Alert>
        
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {receiving?.itemDescription}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lot: {receiving?.lotNumber || 'N/A'} | Available: <strong>{currentAvailable}</strong> units
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label="Quantity to Remove"
              value={adjustForm.quantity}
              onChange={(e) => setAdjustForm(prev => ({ ...prev, quantity: e.target.value }))}
              InputProps={{ inputProps: { min: 1, max: currentAvailable } }}
              helperText={`Max: ${currentAvailable}`}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Reason</InputLabel>
              <Select
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm(prev => ({ ...prev, reason: e.target.value }))}
                label="Reason"
              >
                {ADJUSTMENT_REASONS.map(reason => (
                  <MenuItem key={reason.value} value={reason.value}>
                    {reason.icon} {reason.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              value={adjustForm.notes}
              onChange={(e) => setAdjustForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add any additional details about this adjustment..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setAdjustDialogOpen(false)} disabled={adjusting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdjustSubmit}
          disabled={adjusting || !adjustForm.quantity || !adjustForm.reason}
          startIcon={adjusting ? <CircularProgress size={20} /> : <CheckIcon />}
          sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
        >
          {adjusting ? 'Adjusting...' : 'Confirm Adjustment'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete from Inventory Dialog */}
    <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#ef4444', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>Remove from Inventory</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              This will completely remove this item from inventory
            </Typography>
          </Box>
          <IconButton onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
        
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600}>
            This action cannot be undone!
          </Typography>
          <Typography variant="body2">
            The item will be permanently removed from available inventory. This will be logged in the audit trail for compliance.
          </Typography>
        </Alert>
        
        <Box sx={{ mb: 3, p: 2, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#dc2626' }}>
            {receiving?.rawMaterialItem?.itemDescription}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Qty: {receiving?.qtyConforming} • Lot: {receiving?.lotNumber || 'N/A'}
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Reason for Removal</InputLabel>
              <Select
                value={deleteForm.reason}
                onChange={(e) => setDeleteForm(prev => ({ ...prev, reason: e.target.value }))}
                label="Reason for Removal"
              >
                {ADJUSTMENT_REASONS.map(reason => (
                  <MenuItem key={reason.value} value={reason.value}>
                    {reason.icon} {reason.label}
                  </MenuItem>
                ))}
                <MenuItem value="COMPLETE_REMOVAL">🗑️ Complete Inventory Removal</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Notes"
              value={deleteForm.notes}
              onChange={(e) => setDeleteForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Provide any additional details for audit purposes..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteSubmit}
          disabled={deleting || !deleteForm.reason}
          startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {deleting ? 'Removing...' : 'Permanently Remove'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default RawMaterials;
